import type { SocialPlatform } from "@/hooks/useMarketplace";

/**
 * Placeholder product name. The pivot ships on the ADVisor subdomain until it
 * validates; this is the one place to change when it gets a real name.
 */
export const MARKETPLACE_NAME = "ADVisor Creators";

/** MVP markets. Order is by expected supply, not alphabetical. */
export const COUNTRIES = ["UY", "AR", "MX", "CO", "CL", "GLOBAL"] as const;

export const COUNTRY_LABELS: Record<string, string> = {
  UY: "Uruguay",
  AR: "Argentina",
  MX: "México",
  CO: "Colombia",
  CL: "Chile",
  // Not a country — an account whose own words place it worldwide.
  GLOBAL: "Global",
};

export const PLATFORM_LABELS: Record<SocialPlatform, string> = {
  INSTAGRAM: "Instagram",
  TIKTOK: "TikTok",
  YOUTUBE: "YouTube",
  X: "X",
};

export const PLATFORMS: SocialPlatform[] = ["INSTAGRAM", "TIKTOK", "YOUTUBE", "X"];

/**
 * Niche slugs. Stored on the profile as-is and translated at render through
 * `marketplace.niches.*`, so adding a market's vocabulary is a locale change
 * rather than a migration.
 */
export const NICHES = [
  "belleza",
  "skincare",
  "moda",
  "gastronomia",
  "fitness",
  "bienestar",
  "viajes",
  "tecnologia",
  "gaming",
  "finanzas",
  "negocios",
  "maternidad",
  "hogar",
  "diseno",
  "educacion",
  "deportes",
  "lifestyle",
] as const;

/** Bands rather than a free number — brands think in tiers, not thresholds. */
export const FOLLOWER_BANDS = [
  { value: "", labelKey: "marketplace.filters.anyReach" },
  { value: "1000", labelKey: "marketplace.filters.band1k" },
  { value: "10000", labelKey: "marketplace.filters.band10k" },
  { value: "50000", labelKey: "marketplace.filters.band50k" },
  { value: "100000", labelKey: "marketplace.filters.band100k" },
];

/**
 * Country label, tolerating the unknown case.
 *
 * A discovered profile often carries no location signal, and the pipeline now
 * stores null rather than guessing. The directory must say so plainly — a blank
 * cell reads as a bug, and a defaulted country would be a lie.
 */
export function countryLabel(code: string | null | undefined, unknownLabel: string): string {
  if (!code) return unknownLabel;
  return COUNTRY_LABELS[code] ?? code;
}

export function formatFollowers(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(".0", "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(".0", "")}K`;
  return String(n);
}
